// Modal dialog — the one centred dialog. Portaled to document.body,
// closes on Escape and on a scrim click (unless `dismissible` is false),
// locks page scroll, keeps Tab inside the dialog and hands focus back to
// the opener when it closes. On phones it renders as a bottom Sheet, which
// covers pages the companion hosts.
//
//   <Modal open={open} onClose={close} title="New routine"
//     description="Runs on a schedule and messages you."
//     footer={<><Button variant="ghost" onClick={close}>Cancel</Button>
//              <Button onClick={save}>Save</Button></>}>
//     …form…
//   </Modal>
//
// Put `data-autofocus` on the field that should take focus first
// (default: the first focusable element of the body, else the dialog
// itself). For "are you sure?" use useConfirm() instead.
//
// `placement="top"`: the dialog hangs from a fixed offset near the top
// (and, on a phone, its sheet keeps its full height), so a dialog whose
// content grows as the operator types (a search box with results under
// it) never moves the box: the content grows downwards. With `size="full"`
// it fills nearly the whole window (a phone's sheet nearly the whole
// screen): a large reading view such as the Memory tab's Expand.
//
// `dirty`: the dialog holds typed text the operator would lose. Escape,
// a scrim click and the close X then ask "Discard what you typed?" first
// (phone Sheet too); only Discard closes. Buttons in the footer that call
// onClose themselves (Cancel) are the caller's to guard.

import { useCallback, useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { useIsMobile } from "@/hooks/useMediaQuery";

import { Button } from "./Button";
import { useConfirm } from "./ConfirmModal";
import { OVERLAY_SHADOW, Z, cx } from "./controlStyles";
import { isTopLayer, useFocusTrap, useScrollLock } from "./overlayHooks";
import { Sheet } from "./Sheet";

const WIDTH = {
  sm: "max-w-[400px]",
  md: "max-w-[520px]",
  lg: "max-w-[720px]",
  full: "max-w-[min(1200px,100%)] h-[min(900px,100%)]",
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Action row, right-aligned (secondary first, primary last). */
  footer?: ReactNode;
  size?: keyof typeof WIDTH;
  /** "top": anchored near the top, so growing content never moves what is above it. */
  placement?: "center" | "top";
  /** Element to focus when the dialog opens. */
  initialFocus?: RefObject<HTMLElement>;
  /** false while something is running that must not be abandoned. */
  dismissible?: boolean;
  /** true while the dialog holds typed text: closing asks first. */
  dirty?: boolean;
  className?: string;
}

export function Modal(props: ModalProps) {
  const mobile = useIsMobile();
  const confirm = useConfirm();
  const asking = useRef(false);
  const { onClose, dirty } = props;
  // Escape, the scrim and the X: straight through, or through one
  // "Discard what you typed?" when the dialog holds unsaved text.
  const requestClose = useCallback(async () => {
    if (!dirty) return onClose();
    if (asking.current) return;
    asking.current = true;
    try {
      const discard = await confirm({
        title: "Discard what you typed?",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        danger: true,
      });
      if (discard) onClose();
    } finally {
      asking.current = false;
    }
  }, [dirty, onClose, confirm]);
  if (!props.open) return null;
  const wholeScreen = props.size === "full" && props.placement === "top";
  if (mobile) {
    return (
      <Sheet
        open
        onClose={requestClose}
        title={props.title}
        description={props.description}
        footer={props.footer}
        dismissible={props.dismissible}
        initialFocus={props.initialFocus}
        maxHeightDvh={wholeScreen ? 0.97 : undefined}
        className={cx(props.placement === "top" && (wholeScreen ? "h-[97dvh]" : "h-[90dvh]"), props.className)}
      >
        {props.children}
      </Sheet>
    );
  }
  return <Dialog {...props} onClose={requestClose} />;
}

function Dialog({
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  placement = "center",
  initialFocus,
  dismissible = true,
  className,
}: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useScrollLock(true);
  useFocusTrap(panel, true, initialFocus);

  // Escape closes the innermost dialog only: with a dialog opened from
  // inside another, both listen, and the outer one (registered first)
  // would otherwise close — and unmount — the inner one too.
  useEffect(() => {
    if (!dismissible) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape" || e.defaultPrevented || !isTopLayer(panel.current)) return;
      e.preventDefault();
      onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, dismissible]);

  return createPortal(
    <div
      className={cx(
        "fixed inset-0 flex justify-center bg-[color:var(--c-scrim)] p-4 backdrop-blur-sm md:p-6",
        placement === "top" ? (size === "full" ? "items-start pt-[4vh] md:pt-[4vh]" : "items-start pt-[16vh] md:pt-[16vh]") : "items-center",
      )}
      style={{ zIndex: Z.modal }}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cx(
          "flex w-full flex-col rounded-card border border-line bg-overlay outline-none",
          placement === "top" ? (size === "full" ? "max-h-[92vh]" : "max-h-[80vh]") : "max-h-full",
          OVERLAY_SHADOW,
          WIDTH[size],
          className,
        )}
        style={{ animation: "fadeIn 120ms ease-out both" }}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 px-5 pb-3 pt-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-md font-semibold text-fg">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm text-fg-muted">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1">
              <X aria-hidden className="h-4 w-4" strokeWidth={1.75} />
            </Button>
          )}
        </div>
        <div data-dialog-body="" className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
          {children}
        </div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line-subtle px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
